/**
 * Beginning-of-Year Readiness (v3.46.0; renamed in v3.46.2) — three optional flashcard checks
 * administered ONCE per student per school year: Geometric Shapes, Numbers
 * (numeral identification 1–10), and Colors.
 *
 * Conventions (approved by Kemal, 2026-08-09):
 *  - testType ids are PERMANENT: boy_shapes / boy_numbers / boy_colors
 *    (snake_case, matching 'first_sound' / 'nonsense_word').
 *  - testCategory: 'beginning_of_year' — its own category, deliberately NOT
 *    a literacy strand.
 *  - Once-per-year is structural: score docs live in the shared
 *    `assessments` collection under the deterministic id
 *    boy-{schoolYearStartYear}-{studentId}-{testType} (boyAssessmentDocId).
 *    Create-if-absent, edits update the same doc — a second administration
 *    in the same school year is impossible to record as a duplicate.
 *  - EVERY set is shuffled on EVERY administration (getBoyItems), including
 *    re-entry after an edit — same guess-the-sequence concern as letters.
 *  - Item `id` is the response key (responses: { [id]: 'Correct'|'Incorrect' })
 *    and is as permanent as the testType ids.
 *
 * Card rendering contract (BoyReadiness.js):
 *  - kind 'glyph'  → text card through the normal useFitText path (numerals).
 *  - kind 'shape'  → solid single-color inline SVG, no label.
 *  - kind 'swatch' → large solid color block, no color name anywhere.
 */

export const BOY_TEST_TYPES = ['boy_shapes', 'boy_numbers', 'boy_colors'];

export const BOY_TEST_LABELS = {
  boy_shapes: 'Geometric Shapes',
  boy_numbers: 'Numbers',
  boy_colors: 'Colors',
};

// The sidebar nav group name — also the Assessment Settings toggle key
// (sidebarGroupsHidden entries store this exact string). "4K Readiness"
// per Kemal (v3.47.0) — ONE name everywhere: nav, page, settings, report.
export const BOY_GROUP_NAME = '4K Readiness';

export const BOY_CATEGORY = 'beginning_of_year';

// Shape fill (v3.48.0): ONE warm friendly color for all four shapes — the
// feature's own amber-orange, high-contrast on the pastel card. A single
// constant hue keeps the shapes check from ever reading as a color question
// (owner decision; solid black was too heavy for 4-year-olds, and
// per-shape colors invite "orange!" as an answer).
export const BOY_SHAPE_COLOR = '#f97316';

export const BOY_ITEMS = {
  boy_shapes: [
    { id: 'rectangle', kind: 'shape' },
    { id: 'square', kind: 'shape' },
    { id: 'triangle', kind: 'shape' },
    { id: 'circle', kind: 'shape' },
  ],
  // Numeral identification 1–10 (0 excluded per owner decision; typically
  // taught after 4K numeral ID).
  boy_numbers: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((n) => ({
    id: n,
    kind: 'glyph',
    value: n,
  })),
  // Ten basic colors a 4-year-old is expected to identify (owner-approved
  // set). The hex is the swatch fill; `border: true` marks swatches that need
  // an outline to read against the card (white on pastel yellow).
  boy_colors: [
    { id: 'red', kind: 'swatch', hex: '#E02121' },
    { id: 'blue', kind: 'swatch', hex: '#1E5FD6' },
    { id: 'yellow', kind: 'swatch', hex: '#FFD400' },
    { id: 'green', kind: 'swatch', hex: '#1D9E3F' },
    { id: 'orange', kind: 'swatch', hex: '#F57C00' },
    { id: 'purple', kind: 'swatch', hex: '#7B2FBE' },
    { id: 'black', kind: 'swatch', hex: '#111111' },
    { id: 'brown', kind: 'swatch', hex: '#8B4513' },
    { id: 'pink', kind: 'swatch', hex: '#F28DB8' },
    { id: 'white', kind: 'swatch', hex: '#FFFFFF', border: true },
  ],
};

// ── Observation questions (v3.47.0, owner-approved permanent ids) ─────────
// Two once-per-year Yes/No teacher observations riding the EXACT assessment
// doc scheme: an observation is a 1-item assessment. Yes ≡ 'Correct',
// No ≡ 'Incorrect' (display layers translate); unanswered = doc absent.
// Same deterministic doc id (boyAssessmentDocId), same Redo semantics, same
// demo-overlay behavior — no new storage path, no rules changes.
export const BOY_OBSERVATION_TYPES = ['boy_pencil_grip', 'boy_name_writing'];

export const BOY_OBSERVATION_META = {
  boy_pencil_grip: {
    itemId: 'pencil_grip',
    label: 'Pencil Grip',
    question: 'Does the student have correct pencil grip?',
    icon: '✏️',
  },
  boy_name_writing: {
    itemId: 'name_writing',
    label: 'Name Writing',
    question: 'Can the student write his/her name?',
    icon: '📝',
  },
};

// Dexterity (v3.48.0 as "Handedness", renamed v3.48.2; permanent testType
// boy_handedness stays): a three-state card — no state is "correct", so
// correctCount stays 0 and the value lives as the response itself.
export const BOY_HANDEDNESS_TYPE = 'boy_handedness';
export const BOY_HANDEDNESS_META = {
  itemId: 'handedness',
  label: 'Dexterity',
  question: 'Which hand does the student favor?',
  options: ['left', 'right', 'both'],
  // Short button labels (owner decision) — the question carries the context.
  optionLabels: { left: 'Left', right: 'Right', both: 'Both' },
  badges: { left: 'L', right: 'R', both: 'B' },
  badgeWords: { left: 'Left-handed', right: 'Right-handed', both: 'Uses both hands' },
};

// Notes (v3.48.0, owner-approved permanent id): teacher-only free text, once
// per student per year, its own doc so resume/redo semantics stay uniform.
// The text lives in a `note` field; saving with EMPTY text completes the
// step. NEVER on a family report, an export, or any parent-facing surface —
// shown on the roster popover, read-only in the student pop-up's Teacher
// notes section (v3.96.0) and the Dashboard's notes peek (v3.97.0), and on
// the Student Report, where it prints only on a staff copy (v3.98.0).
export const BOY_NOTES_TYPE = 'boy_notes';
export const BOY_NOTES_META = {
  label: 'Notes',
  prompt: 'Notes for yourself — optional',
};

// The one canonical RUN order (v3.47.0 sequential flow; v3.48.0 additions):
// three flashcard checks, then Name Writing BEFORE Pencil Grip — the
// deliberate owner-specified order: the teacher observes grip while the
// student writes. The RESULTS columns are Pencil-Grip-first — do not "fix"
// this asymmetry (asserted in tests). Handedness and Notes close the run.
export const BOY_SEQUENCE = [
  ...BOY_TEST_TYPES,
  'boy_name_writing',
  'boy_pencil_grip',
  BOY_HANDEDNESS_TYPE,
  BOY_NOTES_TYPE,
];

// Every BOY testType (checks + observations + handedness + notes) — the
// filter set that keeps BOY docs out of quarter-driven surfaces
// (splitBoyAssessments).
export const BOY_ALL_TYPES = BOY_SEQUENCE;

// Teacher-facing prompt shown above each flashcard (small, muted, outside the
// card so it can never be confused with the stimulus).
export const BOY_CARD_PROMPTS = {
  boy_shapes: 'What is this shape?',
  boy_numbers: 'What is this number?',
  boy_colors: 'What is this color?',
};

/**
 * Presentation-ready item list for one check: a fresh shuffled copy on
 * every call (same Fisher–Yates shuffle pattern as assessmentTypes.js). `rng` is a
 * seam for deterministic tests only.
 */
export function getBoyItems(testType, rng = Math.random) {
  const a = [...(BOY_ITEMS[testType] || [])];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * The once-per-year doc id in the `assessments` collection.
 * One administration per student per check per school year, enforced by
 * document identity rather than by UI bookkeeping.
 */
export function boyAssessmentDocId(schoolYearStartYear, studentId, testType) {
  return `boy-${schoolYearStartYear}-${studentId}-${testType}`;
}
